import React, { useEffect, useState } from 'react';
import api from '../api';
import EmptyState from './EmptyState';

// Standings for NHL / NBA / MLB / NFL: divisions by default, switch to conference or league.
// The favourite team is highlighted. Before a season starts, last season's final table.
const GROUP_LABEL = {
  en: { division: 'Division', conference: { nhl: 'Conference', nba: 'Conference', nfl: 'Conference', mlb: 'League' }, league: { nhl: 'League', nba: 'League', nfl: 'League', mlb: 'MLB' } },
  fr: { division: 'Division', conference: { nhl: 'Association', nba: 'Association', nfl: 'Association', mlb: 'Ligue' }, league: { nhl: 'Ligue', nba: 'Ligue', nfl: 'Ligue', mlb: 'MLB' } },
};
const FR_NAMES = {
  'Eastern Conference': 'Association de l’Est', 'Western Conference': 'Association de l’Ouest', Eastern: 'Association de l’Est', Western: 'Association de l’Ouest',
  'American League': 'Ligue américaine', 'National League': 'Ligue nationale', League: 'Ligue',
  'American Football Conference': 'AFC', 'National Football Conference': 'NFC',
};
const EN_NAMES = { Eastern: 'Eastern Conference', Western: 'Western Conference', 'American Football Conference': 'AFC', 'National Football Conference': 'NFC' };

export default function Standings({ sport, lang = 'en', favorite }) {
  const [data, setData] = useState(undefined);
  const [group, setGroup] = useState('division');
  useEffect(() => {
    let alive = true;
    setData(undefined);
    api.fetchStandings(sport).then((d) => { if (alive) setData(d || null); }).catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [sport]);
  const fr = lang === 'fr';
  if (data === undefined) return <EmptyState icon="loading" title={fr ? 'Chargement du classement…' : 'Loading standings…'} />;
  if (!data) return <EmptyState icon="calendar" title={fr ? 'Classement indisponible' : 'Standings unavailable'} hint={fr ? 'Réessaie dans quelques minutes.' : 'Try again in a few minutes.'} />;
  const L = GROUP_LABEL[fr ? 'fr' : 'en'];
  const groupName = (n) => (fr ? FR_NAMES[n] || n : EN_NAMES[n] || n);
  const groups = data.groups[group] || [];
  return (
    <div className="st-wrap">
      <div className="st-top">
        <div className="f1-page-tabs st-switch">
          {['division', 'conference', 'league'].map((g) => (
            <button key={g} type="button" className={group === g ? 'active' : ''} onClick={() => setGroup(g)}>
              {g === 'division' ? L.division : L[g][sport]}
            </button>
          ))}
        </div>
        <span className="st-season">{data.final ? (fr ? `Saison ${data.season} · final` : `${data.season} season · final`) : (fr ? `Saison ${data.season}` : `${data.season} season`)}</span>
      </div>
      {groups.map((g) => (
        <div key={g.name} className="game-card st-card">
          <div className="st-head">
            <b>{groupName(g.name)}</b>
          </div>
          <div className="st-table" style={{ '--cols': data.columns.length }}>
            <div className="st-row st-row-head">
              <span className="st-rank" />
              <span className="st-team">{fr ? 'Équipe' : 'Team'}</span>
              {data.columns.map(([k, label]) => <span key={k} className={`st-num st-${k}`}>{label}</span>)}
            </div>
            {g.teams.map((t) => (
              <div key={t.abbrev} className={`st-row${t.abbrev === favorite ? ' fav' : ''}`}>
                <span className="st-rank">{t.rank}</span>
                <span className="st-team">
                  {t.logo && <img className={`st-logo ${sport}`} alt="" src={t.logo} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />}
                  <b className="st-name">{t.name}</b>
                  <b className="st-abbrev">{t.abbrev}</b>
                  {t.clinch && <small className="st-clinch">{t.clinch}</small>}
                </span>
                {data.columns.map(([k]) => {
                  const v = t[k];
                  const neg = (k === 'diff' && String(v).startsWith('-')), pos = (k === 'diff' && /^\+?[1-9]/.test(String(v)) && !String(v).startsWith('-'));
                  return <span key={k} className={`st-num st-${k}${neg ? ' neg' : ''}${pos ? ' pos' : ''}`}>{k === 'diff' && pos && !String(v).startsWith('+') ? `+${v}` : v}</span>;
                })}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
